import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FilmSlate, Play } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, Confirm } from "@/components/common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, fmtDateTime, errMsg } from "@/lib/api";

export default function ProductVideo({ product }) {
  const [note, setNote] = useState("");
  const [ratio, setRatio] = useState("16:9");
  const [task, setTask] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    api.get(`/products/${product.id}/videos`).then((r) => setTask(r.data.rows[0] || null)).catch(() => {});
    return () => clearInterval(timer.current);
  }, [product.id]);

  useEffect(() => {
    clearInterval(timer.current);
    if (task?.status !== "running" && task?.status !== "queued") return undefined;
    timer.current = setInterval(async () => {
      try {
        const r = await api.get(`/video-tasks/${task.id}`);
        setTask(r.data);
        if (r.data.status === "completed") toast.success("Tanıtım videosu hazır");
        if (r.data.status === "failed") toast.error(r.data.error || "Video üretilemedi");
      } catch { /* keep polling */ }
    }, 5000);
    return () => clearInterval(timer.current);
  }, [task?.id, task?.status]);

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/products/${product.id}/video`, { note, aspect_ratio: ratio, duration: "8s", generate_audio: true });
      setTask(r.data);
      toast.success("Video hazırlanıyor, 1-2 dakika sürebilir");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  const running = task?.status === "running" || task?.status === "queued";

  return (
    <div className="panel p-6" data-testid="product-video-panel">
      <h3 className="mb-1 flex items-center gap-2 font-display text-lg font-bold"><FilmSlate size={18} /> Tanıtım Videosu</h3>
      <p className="mb-4 text-[11px] text-muted-foreground">Ürün için 8 saniyelik tanıtım videosu üretilir. Müşterilerinize WhatsApp veya sosyal medyada paylaşabilirsiniz.</p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="sm:col-span-2">
          <Field label="Videoda vurgulanmasını istediğiniz detay (opsiyonel)">
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Örn. 25 kg'lık çuval, tarla uygulaması" disabled={running} data-testid="video-note-input" />
          </Field>
        </div>
        <Field label="Format">
          <Select value={ratio} onValueChange={setRatio} disabled={running}>
            <SelectTrigger data-testid="video-ratio-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="16:9">Yatay (16:9)</SelectItem>
              <SelectItem value="9:16">Dikey (9:16 · Reels)</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>

      {task?.status === "completed" ? (
        <div className="mt-4">
          <Confirm title="Yeni video oluşturulsun mu?"
            description="Bu ürün için yeni bir video üretilecek. Her video üretimi Universal Key bakiyenizden düşer."
            onConfirm={start} testid="video-confirm-button"
            trigger={<Button disabled={busy || running} className="rounded-[4px] bg-teal-700 hover:bg-teal-800" data-testid="video-generate-button"><Play /> Yeni Video Oluştur</Button>} />
        </div>
      ) : (
        <Button onClick={start} disabled={busy || running} className="mt-4 rounded-[4px] bg-teal-700 hover:bg-teal-800" data-testid="video-generate-button">
          <Play /> {running ? "Hazırlanıyor…" : busy ? "Başlatılıyor…" : "Video Oluştur"}
        </Button>
      )}

      {running && (
        <div className="mt-4 border border-teal-200 bg-teal-50 p-3 text-sm text-teal-800" data-testid="video-progress">
          Video üretiliyor, bu işlem 1-2 dakika sürebilir. Sayfada kalabilir veya sonra geri dönebilirsiniz.
        </div>
      )}
      {task?.status === "failed" && (
        <div className="mt-4 border border-red-300 bg-red-50 p-3 text-sm text-red-700" data-testid="video-error">{task.error || "Video üretilemedi"}</div>
      )}
      {task?.status === "completed" && task.video_url && (
        <div className="mt-4 space-y-2" data-testid="video-result">
          <video src={task.video_url} controls className="w-full max-w-xl border border-border" data-testid="video-player" />
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span>Oluşturma: {fmtDateTime(task.created_at)}</span>
            <a href={task.video_url} target="_blank" rel="noreferrer" className="text-teal-700 underline-offset-2 hover:underline" data-testid="video-download-link">Videoyu indir / paylaş</a>
          </div>
        </div>
      )}
    </div>
  );
}
